/**
 * Helpers de los gráficos SVG (hechos a mano, sin librerías).
 *
 * La regla del portal: los gráficos son para LEER, no para impresionar. Pocas
 * marcas en los ejes, sin sombras ni 3D, una sola familia tipográfica, y el
 * número siempre disponible (visible o en el `<title>` nativo del SVG). Todo
 * lo que se puede calcular sin DOM vive acá y se testea.
 */

export interface Point {
  x: number;
  y: number;
}

export function clamp(value: number, min: number, max: number): number {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

/**
 * Escala lineal dominio→rango. Dominio degenerado (min === max) ⇒ todo cae en
 * el principio del rango, en vez de dividir por cero.
 */
export function linearScale(
  domain: readonly [number, number],
  range: readonly [number, number],
): (value: number) => number {
  const [d0, d1] = domain;
  const [r0, r1] = range;
  const span = d1 - d0;
  if (span === 0) return () => r0;
  return (value: number) => r0 + ((value - d0) / span) * (r1 - r0);
}

/** Paso "lindo" (1, 2, 5 × 10^n) para un rango y una cantidad de marcas. */
export function niceStep(span: number, count: number): number {
  if (span <= 0 || count <= 0) return 1;
  const rough = span / count;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const normalized = rough / magnitude;
  // Se elige el múltiplo MÁS CERCANO (no el siguiente hacia arriba): con
  // "hacia arriba", un rango 0..1 con 4 marcas caía en pasos de 0.5 y el eje
  // quedaba con dos marcas.
  const step = normalized <= 1.5 ? 1 : normalized <= 3 ? 2 : normalized <= 7 ? 5 : 10;
  return step * magnitude;
}

/**
 * Marcas de eje: múltiplos del paso dentro de [min, max], pocas y redondas.
 * Nunca devuelve una lista vacía (siempre entra al menos `min`).
 */
export function niceTicks(min: number, max: number, count = 4): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [0];
  if (min === max) return [min];
  const step = niceStep(max - min, count);
  const start = Math.ceil(min / step) * step;
  const out: number[] = [];
  // El epsilon evita perder la última marca por error de coma flotante.
  for (let v = start; v <= max + step * 1e-9; v += step) {
    out.push(Math.abs(v) < step * 1e-9 ? 0 : Number(v.toFixed(10)));
  }
  return out.length > 0 ? out : [min];
}

/** Polilínea SVG (`M x y L x y …`). Lista vacía ⇒ path vacío. */
export function linePath(points: readonly Point[]): string {
  if (points.length === 0) return '';
  return points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${round(p.x)},${round(p.y)}`)
    .join(' ');
}

function round(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Ancho de una barra dentro de un track, como fracción del máximo.
 * Máximo 0 ⇒ 0 (no se dibuja nada, que es lo honesto con muestra vacía).
 */
export function barWidth(value: number, max: number, trackWidth: number): number {
  if (max <= 0 || trackWidth <= 0) return 0;
  return clamp(value / max, 0, 1) * trackWidth;
}

// ---------------------------------------------------------------------------
// Heatmap: escala secuencial por buckets
// ---------------------------------------------------------------------------

/** Cantidad de pasos de la rampa (coincide con las --heat-N del CSS). */
export const HEAT_STEPS = 6;

/**
 * Bucket 0..steps-1 de un valor dentro de [min, max]. Rango degenerado ⇒ el
 * bucket del medio: con un solo valor no hay gradiente que mostrar, y pintar
 * todo del color más intenso mentiría.
 */
export function heatBucket(value: number, min: number, max: number, steps = HEAT_STEPS): number {
  if (steps <= 1) return 0;
  if (!Number.isFinite(value)) return 0;
  if (max <= min) return Math.floor(steps / 2);
  const t = clamp((value - min) / (max - min), 0, 1);
  return clamp(Math.floor(t * steps), 0, steps - 1);
}

/** Extremos de una serie; vacía ⇒ [0, 0]. */
export function extent(values: readonly number[]): [number, number] {
  if (values.length === 0) return [0, 0];
  let min = Infinity;
  let max = -Infinity;
  for (const v of values) {
    if (!Number.isFinite(v)) continue;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  if (min === Infinity || max === -Infinity) return [0, 0];
  return [min, max];
}

/** Variable CSS del bucket (definidas en styles.css como `--heat-0`…). */
export function heatVar(bucket: number): string {
  return `var(--heat-${clamp(Math.round(bucket), 0, HEAT_STEPS - 1)})`;
}

/**
 * Celdas de la grilla del heatmap: producto de las zonas presentes, con
 * `null` (zona sin codificar) al final de cada eje.
 */
export function zoneAxis(values: ReadonlyArray<number | null>): Array<number | null> {
  const numbers = new Set<number>();
  let hasNull = false;
  for (const v of values) {
    if (v === null) hasNull = true;
    else numbers.add(v);
  }
  const sorted: Array<number | null> = [...numbers].sort((a, b) => a - b);
  if (hasNull) sorted.push(null);
  return sorted;
}

export function zoneLabel(zone: number | null): string {
  return zone === null ? 's/d' : String(zone);
}
